import React, {
  useState,
  useImperativeHandle,
  useEffect,
  forwardRef,
  useRef,
} from "react"
import Api from "../../../../_helpers/Api"
import { trans } from "../../../../_providers/Translation"
import servicingHeadImage from "@/assets/img/contractors/icons/servicing-head.svg"

//images
import loader from "../../../../assets/img/loader.svg"
import Error from "../../../modals/messages/Error"
import Dropdown from "@/components/partials/Dropdown"
import Success from "../../../modals/messages/Success"
import { FormControlLabel } from "@mui/material"
import BlueSwitch from "@/components/partials/BlueSwitch"
import ServicingAction from "./ServicingAction"
import moment from "moment"
import DatePicker from "@/components/partials/Date"
import Skeleton from "react-loading-skeleton"

function Servicing(props, ref) {
  const [popup, setPopup] = useState({
    overlay: false,
    modal: false,
    loading: false,
    data: null,
    id: null,
    edit: false,
    loadingData: false,
  })

  const [validations, setValidations] = useState(null)
  const [actions, setActions] = useState([])

  const successModalRef = useRef(null)
  const errorModalRef = useRef(null)
  const subscriptionDropdownRef = useRef(null)

  useImperativeHandle(ref, () => ({
    open: (id) => {
      handleShow(id)
    },
    hide: () => {
      handleHide()
    },
  }))

  useEffect(() => {
    if (popup.edit) {
      loadData()
    }
  }, [popup.edit])

  const loadData = () => {
    if (!popup.id) {
      return
    }

    setPopup((prev) => ({
      ...prev,
      loadingData: true,
    }))

    Api.get(`contractors/appliance-servicing/${popup.id}`)
      .then((res) => {
        setPopup((prev) => ({
          ...prev,
          data: {
            ...res.data,
            subscription: {
              ...(res.data.subscription || {}),
              currency: res.data.subscription?.type?.currency_name || "",
            },
          },
        }))
        // Load actions from data if available
        if (
          res.data?.servicing_actions &&
          Array.isArray(res.data.servicing_actions)
        ) {
          setActions(
            res.data.servicing_actions.map((action, idx) => ({
              ...action,
              _key: action.id || Date.now() + idx,
            })),
          )
        }
      })
      .finally(() => {
        setPopup((prev) => ({
          ...prev,
          loadingData: false,
        }))
      })
  }

  const handleShow = (id = null) => {
    subscriptionDropdownRef.current?.refetchData()

    setPopup((prev) => ({
      ...prev,
      id: id,
      edit: id ? new Date() : null,
    }))

    show()
  }

  const handleHide = (e) => {
    e.preventDefault()

    setPopup((prev) => ({
      ...prev,
      id: null,
      data: null,
      edit: false,
    }))

    setValidations(null)
    setActions([])

    hide().then(() => {
      if (typeof props.onClose === "function") {
        props.onClose()
      }
    })
  }

  const show = async () => {
    return new Promise((resolve, reject) => {
      setPopup((prev) => ({
        ...prev,
        overlay: true,
      }))

      setTimeout(() => {
        setPopup((prev) => ({
          ...prev,
          overlay: true,
          modal: true,
        }))

        return resolve()
      }, 50)
    })
  }

  const hide = async () => {
    return new Promise((resolve, reject) => {
      setPopup((prev) => ({
        ...prev,
        modal: false,
        loading: false,
      }))

      setTimeout(() => {
        setPopup((prev) => ({
          ...prev,
          overlay: false,
          modal: false,
        }))

        return resolve()
      }, 50)
    })
  }

  const handleSave = (e) => {
    e.preventDefault()

    setPopup((prev) => ({
      ...prev,
      loading: true,
    }))

    setValidations(null)

    let url = "contractors/appliance-servicing/" + (popup.id ? "edit" : "add")

    let data = new FormData(e.target)

    if (popup.id) {
      data.append("id", popup.id)
    }

    Api.post(url, data)
      .then((res) => {
        handleHide(e)

        if (typeof props.onSave === "function") {
          props.onSave(res.data)
        }
      })
      .catch((error) => {
        setPopup((prev) => ({
          ...prev,
          loading: false,
        }))

        let _err = error.response

        if (_err && _err.status && _err.status == 422) {
          for (let [input, err] of Object.entries(_err.data.errors)) {
            setValidations((prevState) => {
              return { ...prevState, [input]: true }
            })
          }
        }
      })
  }

  const handleInputChange = (e) => {
    let name = e.target.name
    let val = e.target.value

    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: val,
      },
    }))
  }

  const handleSubscriptionChange = (data) => {
    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        subscription: data,
      },
    }))
  }

  function handleSwitchChange(e) {
    const { name, checked } = e.target

    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: checked ? 1 : 0,
      },
    }))
  }

  const updateActionField = (index, field, value) => {
    setActions((prev) => {
      const next = prev.slice()
      next[index] = { ...(next[index] || {}), [field]: value }
      return next
    })
  }

  const handleAddAction = () => {
    setActions((prev) => [...prev, { _key: Date.now() }])
  }

  const handleRemoveAction = (index) => {
    setActions((prev) => {
      const next = prev.slice()
      next.splice(index, 1)
      return next
    })
  }

  return (
    <>
      <Success ref={successModalRef} mainMessage={trans("headings.success6")} />

      <Error ref={errorModalRef} mainMessage={trans("headings.error3")} />
      <div className={`${popup.overlay ? "visible" : ""} overlay`}>
        <form
          className={`${popup.modal ? "show" : ""} popup-primary popup-subscription-type medium`}
          onSubmit={handleSave}
        >
          {popup.data?.contractor_id ? (
            <input
              type="hidden"
              name="contractor_id"
              value={popup.data?.contractor_id}
            />
          ) : (
            <></>
          )}

          <div className="head-new" style={{ marginBottom: "10px" }}>
            <img src={servicingHeadImage} alt="icon" />
            <div className="texts">
              <h2>{trans("headings.servicing")}</h2>
              <p>{trans("subheadings.servicing")}</p>
            </div>
            <span className="close" onClick={handleHide}></span>
          </div>

          {popup.loadingData ? (
            <Skeleton height={45} count={5} />
          ) : (
            <>
              <div className="row">
                <div className="col">
                  <label>{trans("labels.name")}</label>
                  <input
                    type="text"
                    name="name"
                    value={popup.data?.name || ""}
                    onChange={handleInputChange}
                    className={
                      validations && validations.name ? "validate" : ""
                    }
                  />
                </div>
                <div className="col">
                  <label>{trans("labels.subscription")}</label>
                  <Dropdown
                    inputName=""
                    inputValue={popup.data?.subscription?.name || ""}
                    inputIdName="subscription_id"
                    inputIdValue={popup.data?.subscription?.id || ""}
                    url="contractors/subscriptions/list"
                    params={{
                      id: popup.data?.subscription?.id || null,
                      limit: 100,
                    }}
                    inputClassName={
                      validations && validations.subscription_id
                        ? "validate"
                        : ""
                    }
                    ref={subscriptionDropdownRef}
                    onChange={handleSubscriptionChange}
                  />
                </div>
                <div
                  className="col"
                  style={{
                    width: "fit-content",
                  }}
                >
                  <label>{trans("labels.dateOfFirstServicing")}</label>
                  <DatePicker
                    name="date_of_first_servicing"
                    date={
                      popup.data?.date_of_first_servicing ||
                      moment().format("YYYY-MM-DD")
                    }
                    validate={Boolean(
                      validations && validations.date_of_first_servicing
                    )}
                    onDateChange={(date) =>
                      handleInputChange({
                        target: { name: "date_of_first_servicing", value: date || "" },
                      })
                    }
                    fullWidth
                  />
                </div>
              </div>
              {popup.data?.subscription ? (
                <>
                  <hr />
                  <div className="row">
                    <div className="col">
                      <label htmlFor="">{trans("labels.selectedSubscription")}</label>
                      <table>
                        <tbody>
                          <tr>
                            <td>{trans("tables.expiresOn")}</td>
                            <td>{popup.data?.subscription?.expires_at_dmy}</td>
                          </tr>
                          <tr>
                            <td>{trans("random.activeStatus")}</td>
                            <td>
                              {trans(
                                `tables.${popup.data?.subscription?.is_active ? "yes" : "no"}`,
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>{trans("random.withValue")}</td>
                            <td>
                              {`${(
                                popup.data?.subscription?.type?.price || 0
                              ).toFixed(
                                2,
                              )} ${popup.data?.subscription?.currency?.name || popup.data?.subscription?.currency_name || ""}`}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <hr />
                </>
              ) : (
                <></>
              )}
              <div className="row">
                <div className="col">
                  {actions.map((action, index) => (
                    <ServicingAction
                      key={action.id || action._key || index}
                      action={action}
                      index={index}
                      onUpdateField={updateActionField}
                      onRemove={handleRemoveAction}
                      validations={validations}
                    />
                  ))}
                  <div className="row">
                    <button
                      className="button-add button-small"
                      type="button"
                      onClick={handleAddAction}
                    >
                      {trans("buttons.newActivity")}
                    </button>
                  </div>
                </div>
              </div>
              <hr />
              <div className="row">
                <div className="col">
                  <FormControlLabel
                    control={
                      <BlueSwitch
                        name="automatic_invoicing_on"
                        checked={
                          popup.data?.automatic_invoicing_on ? true : false
                        }
                        onChange={handleSwitchChange}
                        value={popup.data?.automatic_invoicing_on}
                      />
                    }
                    label={trans("labels.autoInvoicing")}
                  />
                </div>
              </div>
            </>
          )}

          <div className="footer align-right">
            {popup.loading ? (
              <img src={loader} alt="loading" />
            ) : (
              <>
                <button className="button" onClick={handleHide}>
                  {trans("buttons.cancel")}
                </button>
                <button className="button button-primary" type="submit">
                  {trans("buttons.save")}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  )
}

export default forwardRef(Servicing)
